import { useState, useRef, useEffect } from "react";
import { t, font } from "@realtyflows/ui";

/**
 * SearchSelect — searchable dropdown select.
 *
 * Props:
 *   value       — selected value
 *   onChange     — (value) => void
 *   options      — [{ value, label, subtitle? }]
 *   label        — label above
 *   placeholder  — placeholder when nothing selected
 *   emptyLabel   — text for the "none" option (e.g., "-- New contact --")
 *   allowEmpty   — show empty/none option (default true)
 */
export default function SearchSelect({ value, onChange, options = [], label, placeholder, emptyLabel, allowEmpty = true }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setSearch(""); } };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (open && searchRef.current) searchRef.current.focus();
  }, [open]);

  const selected = options.find((o) => String(o.value) === String(value));
  const q = search.toLowerCase();
  const filtered = options.filter((o) =>
    !q || (o.label || "").toLowerCase().includes(q) || (o.subtitle || "").toLowerCase().includes(q)
  );

  return (
    <div ref={ref} style={{ position: "relative" }}>
      {label && (
        <div style={{ fontSize: 11, color: t.textSecondary, marginBottom: 4, fontWeight: 600, fontFamily: font.body }}>
          {label}
        </div>
      )}

      {/* Trigger */}
      <div
        onClick={() => { setOpen(!open); setSearch(""); }}
        style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          background: t.surfaceWarm, border: `1.5px solid ${open ? t.emerald : t.border}`,
          borderRadius: 6, padding: "8px 10px", cursor: "pointer",
          transition: "border-color 0.15s, box-shadow 0.15s",
          boxShadow: open ? `0 0 0 3px ${t.emerald}18` : "none",
          minHeight: 36, boxSizing: "border-box",
        }}
      >
        <span style={{
          fontSize: 12, fontFamily: font.body,
          color: selected ? t.textPrimary : t.textMuted,
          fontWeight: selected ? 500 : 400,
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>
          {selected ? selected.label : (placeholder || emptyLabel || "Select...")}
        </span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={t.textMuted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
          style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 0.15s", flexShrink: 0 }}>
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </div>

      {/* Dropdown */}
      {open && (
        <div style={{
          position: "absolute", top: "100%", left: 0, right: 0, zIndex: 50,
          marginTop: 4, background: t.surface, border: `1.5px solid ${t.border}`,
          borderRadius: 10, boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
          maxHeight: 280, overflow: "hidden", display: "flex", flexDirection: "column",
        }}>
          {/* Search input */}
          <div style={{ padding: "8px 10px", borderBottom: `1px solid ${t.border}` }}>
            <input
              ref={searchRef}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search..."
              style={{
                width: "100%", boxSizing: "border-box", background: t.surfaceWarm,
                border: `1px solid ${t.border}`, borderRadius: 6, padding: "7px 10px",
                fontSize: 12, color: t.textPrimary, outline: "none", fontFamily: font.body,
              }}
            />
          </div>

          {/* Options */}
          <div style={{ overflowY: "auto", flex: 1 }}>
            {allowEmpty && !q && (
              <div
                onClick={() => { onChange(""); setOpen(false); setSearch(""); }}
                style={{
                  padding: "8px 14px", cursor: "pointer", fontSize: 12, color: t.textMuted,
                  fontFamily: font.body, fontStyle: "italic",
                  background: !value ? `${t.emerald}08` : "transparent",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = t.surfaceWarm; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = !value ? `${t.emerald}08` : "transparent"; }}
              >
                {emptyLabel || "-- None --"}
              </div>
            )}
            {filtered.length === 0 && (
              <div style={{ padding: "16px 14px", textAlign: "center", fontSize: 12, color: t.textMuted, fontFamily: font.body }}>
                No results for "{search}"
              </div>
            )}
            {filtered.map((opt) => {
              const isSelected = String(opt.value) === String(value);
              return (
                <div
                  key={opt.value}
                  onClick={() => { onChange(opt.value); setOpen(false); setSearch(""); }}
                  style={{
                    padding: "8px 14px", cursor: "pointer",
                    background: isSelected ? `${t.emerald}08` : "transparent",
                    borderLeft: isSelected ? `2.5px solid ${t.emerald}` : "2.5px solid transparent",
                    transition: "background 0.1s",
                  }}
                  onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = t.surfaceWarm; }}
                  onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = "transparent"; }}
                >
                  <div style={{ fontSize: 12, fontWeight: isSelected ? 600 : 400, color: t.textPrimary, fontFamily: font.body }}>
                    {opt.label}
                  </div>
                  {opt.subtitle && (
                    <div style={{ fontSize: 10, color: t.textMuted, fontFamily: font.body, marginTop: 1 }}>
                      {opt.subtitle}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
